<template>
  <v-dialog v-model="dialog" max-width="600" @keydown.esc="cancel()" persistent :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title class="white--text">{{ newType ? $t('item_types.create_new') : $t('item_types.edit') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-5">
        <v-form ref="form">
          <v-text-field
            :disabled="saving"
            outlined
            dense
            :label="$t('item_types.name')"
            :rules="[$rules.required]"
            v-model="type.name"
            validate-on-blur
          />
          <v-text-field
            :disabled="saving"
            outlined
            dense
            :label="$t('item_types.plural_name')"
            :hint="$t('item_types.plural_name_hint')"
            persistent-hint
            :rules="[$rules.required]"
            v-model="type.pluralName"
            validate-on-blur
          />
          <div class="subtitle-2 mt-4">{{ $t('item_types.purpose') }}</div>
          <v-radio-group
            v-model="type.purpose"
            :disabled="saving || purposeLocked"
            :hint="purposeLocked ? $t('item_types.purpose_locked') : ''"
            persistent-hint
            class="mt-1"
          >
            <v-radio v-for="purpose in purposes" :key="purpose" :label="$t('item_types.purposes.' + purpose)" :value="purpose" />
          </v-radio-group>
          <div class="subtitle-2 mt-4">{{ $t('item_types.accounting_class') }}</div>
          <v-radio-group v-model="type.accountingClass" :disabled="saving" class="mt-1">
            <v-radio v-for="accountingClass in accountingClasses" :key="accountingClass" :value="accountingClass">
              <template v-slot:label>
                <div>
                  <div>{{ $t('item_types.accounting_classes.' + accountingClass) }}</div>
                  <div class="caption text--secondary">{{ $t('item_types.accounting_class_hint.' + accountingClass) }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          <template v-if="type.purpose === 'MATERIAL'">
            <div class="subtitle-2 mt-4">{{ $t('item_types.features') }}</div>
            <v-checkbox
              v-for="feature in features"
              :key="feature"
              v-model="type[feature]"
              :disabled="saving"
              :label="$t('item_types.feature.' + feature)"
              :hint="$t('item_types.feature_hint.' + feature)"
              persistent-hint
              class="mt-2"
            />
          </template>
          <v-switch
            :disabled="saving"
            inset
            class="mt-6"
            :label="$t('item_types.active')"
            :hint="$t('item_types.active_hint')"
            persistent-hint
            v-model="type.active"
          />
        </v-form>
        <v-alert :value="showAlert" outlined type="error" class="mt-4">{{ $t('validation.check_all_required_fields') }}</v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="default" @click.native="cancel()">{{ $t('cancel') }}</v-btn>
        <v-btn color="primary" :loading="saving" @click.native="save()">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import itemTypeApi from '@/api/itemType';
import { ACCOUNTING_CLASS, FEATURES, PURPOSE } from '@/helpers/itemTypes';

const typeTemplate = {
  id: null,
  name: '',
  pluralName: '',
  purpose: PURPOSE.MATERIAL,
  accountingClass: ACCOUNTING_CLASS.VAIHTOOMAISUUS,
  trackInventory: false,
  showUnit: false,
  showWarehouseUnit: false,
  pipe: false,
  siteAdditionalItem: false,
  active: true,
  itemCount: 0,
};

export default {
  name: 'ItemTypeDialog',
  data() {
    return {
      dialog: false,
      saving: false,
      showAlert: false,
      newType: true,
      type: { ...typeTemplate },
      purposes: Object.values(PURPOSE),
      accountingClasses: Object.values(ACCOUNTING_CLASS),
      features: FEATURES,
    };
  },
  computed: {
    // Nimikkeelliseltä tyypiltä ei vaihdeta käyttötarkoitusta (backend palauttaa 409)
    purposeLocked() {
      return !this.newType && this.type.itemCount > 0;
    },
  },
  methods: {
    open(existingType) {
      this.dialog = true;
      this.showAlert = false;
      this.newType = existingType == null;
      this.$refs.form?.resetValidation();
      this.type = { ...typeTemplate, ...(existingType || {}) };
    },
    cancel() {
      this.dialog = false;
      this.type = { ...typeTemplate };
      this.showAlert = false;
    },
    async save() {
      if (!this.type.name || !this.type.pluralName) {
        this.showAlert = true;
        return;
      }
      const material = this.type.purpose === PURPOSE.MATERIAL;
      const payload = {
        name: this.type.name,
        pluralName: this.type.pluralName,
        purpose: this.type.purpose,
        accountingClass: this.type.accountingClass,
        active: this.type.active,
      };
      FEATURES.forEach(feature => (payload[feature] = material && this.type[feature]));
      try {
        this.saving = true;
        if (this.newType) {
          await itemTypeApi.createItemType(payload);
          this.$emit('created');
        } else {
          await itemTypeApi.updateItemType(this.type.id, payload);
          this.$emit('updated');
        }
        this.cancel();
      } catch (err) {
        const status = err.response && err.response.status;
        if (status === 409) {
          this.$showErrorNotification(this.$t('item_types.errors.purpose_locked'));
        } else if (status === 400) {
          this.$showErrorNotification(this.$t('item_types.errors.duplicate_name'));
        } else {
          this.$handleApiError(err);
        }
      }
      this.saving = false;
    },
  },
};
</script>
